import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import styled from "styled-components";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";

import "@inovua/reactdatagrid-enterprise/base.css";
import "../../assets/default-light.scss";

import {
  exportCSV
} from "../reports/ExportCSV";

export default function Table({
  columns = [],
  data = [],
  settings,
  groups,
  noDirectives = false,
  defaultLimit,
  fileName,
  downloadList = false,
  totalCount,
  emptyText,
  hideToolbar = false,
  variant = "default",
  pagination,
  style,
  downloadTrigger = 0,
  ...gridProps
}) {
  const [
    gridRef,
    setGridRef
  ] = useState(null);

  const configuredColumns =
      useMemo(() => {
        const nextColumns =
            columns.map(column => ({
              ...column
            }));

        if (!settings) {
          return nextColumns.map(
              column => ({
                ...column,
                textAlign:
                    column.textAlign ??
                    "center"
              })
          );
        }

        const defaultSetting =
            settings.default ?? {};

        nextColumns.forEach(
            column => {
              Object.assign(
                  column,
                  defaultSetting
              );
            }
        );

        (
            settings.setColumns ?? []
        ).forEach(setting => {
          const targetColumn =
              nextColumns[
                  setting.target
                  ];

          if (!targetColumn) {
            return;
          }

          Object.assign(
              targetColumn,
              setting.value ?? {},
              setting.function ?? {}
          );
        });

        return nextColumns;
      }, [columns, settings]);

  useEffect(() => {
    const gridApi =
        gridRef?.current ??
        gridRef;

    if (
        typeof gridApi
            ?.setColumnSizesToFit ===
        "function"
    ) {
      gridApi.setColumnSizesToFit();
    }
  }, [
    gridRef,
    configuredColumns
  ]);

  const i18n = useMemo(
      () => ({
        ...ReactDataGrid
            .defaultProps.i18n,
        sortAsc: "오름차순",
        sortDesc: "내림차순",
        unsort: "정렬 해제",
        autoSizeToFit:
            "크기에 맞게 자동 조정",
        autoresizeThisColumn:
            "열 자동 조정",
        autoresizeAllColumns:
            "모든 열 자동 조정",
        columns: "항목 설정",
        pageText: "페이지",
        ofText: "/",
        perPageText: "페이지 당",
        showingText: "페이지"
      }),
      []
  );

  const handleExport = () => {
    if (!gridRef) {
      return;
    }

    exportCSV(
        gridRef,
        fileName
    );
  };

  useEffect(() => {
    if (downloadTrigger > 0) {
      handleExport();
    }
    // gridRef는 현재 테이블 인스턴스를 사용하며, 외부 트리거가 변경될 때만 내보냅니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTrigger]);

  const renderRowContextMenu =
      menuProps => {
        menuProps.autoDismiss = true;
      };

  const count =
      totalCount?.[0] ?? 0;

  const countLabel =
      totalCount?.[1] ??
      "데이터";

  const emptyContent = (
      <EmptyState>
        <EmptyIcon
            aria-hidden="true"
        >
          <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
          >
            <path
                fill="currentColor"
                d="M4 5h16v14H4V5Zm2 2v10h12V7H6Zm2 2h8v2H8V9Zm0 4h5v2H8v-2Z"
            />
          </svg>
        </EmptyIcon>

        <EmptyTitle>
          {emptyText ??
              "데이터가 없습니다."}
        </EmptyTitle>

        <EmptyDescription>
          조회 조건을 변경하거나
          다시 검색해주세요.
        </EmptyDescription>
      </EmptyState>
  );

  return (
      <TableContainer
          $variant={variant}
      >
        {!hideToolbar && (
            <TableToolbar>
              <ToolbarLeft>
                {totalCount && (
                    <TotalCount>
                      <TotalCountLabel>
                        총
                      </TotalCountLabel>

                      <SummaryCount>
                        {count}
                      </SummaryCount>

                      <TotalCountText>
                        건의 {countLabel}
                      </TotalCountText>
                    </TotalCount>
                )}
              </ToolbarLeft>

              <ToolbarRight>
                {!noDirectives && (
                    <ScrollGuide>
                      <ScrollGuideIcon
                          aria-hidden="true"
                      >
                        ⇧
                      </ScrollGuideIcon>

                      Shift + 스크롤로
                      좌우 이동
                    </ScrollGuide>
                )}

                {downloadList && (
                    <ExcelButton
                        type="button"
                        onClick={handleExport}
                    >
                      <ExcelIcon
                          aria-hidden="true"
                      >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                        >
                          <path
                              fill="currentColor"
                              d="M5 2h10l4 4v16H5V2Zm2 2v16h10V7h-3V4H7Zm2 6h6v2H9v-2Zm0 4h6v2H9v-2Z"
                          />
                        </svg>
                      </ExcelIcon>

                      엑셀 저장
                    </ExcelButton>
                )}
              </ToolbarRight>
            </TableToolbar>
        )}

        <GridFrame
            $variant={variant}
        >
          <StyledDataGrid
              licenseKey={
                process.env
                    .REACT_APP_DATA_GRID_LICENSE_KEY
              }
              i18n={i18n}
              handle={setGridRef}
              columns={configuredColumns}
              dataSource={data}
              headerHeight={44}
              rowHeight={46}
              showZebraRows={true}
              showCellBorders="horizontal"
              groups={
                groups !== null
                    ? groups
                    : false
              }
              enableColumnAutosize={true}
              showColumnMenuLockOptions={
                false
              }
              showColumnMenuGroupOptions={
                false
              }
              emptyText={emptyContent}
              defaultLimit={defaultLimit}
              pagination={pagination}
              showHoverRows={true}
              activeCell={null}
              renderRowContextMenu={
                renderRowContextMenu
              }
              style={{
                minHeight: 350,
                ...style
              }}
              {...gridProps}
          />
        </GridFrame>
      </TableContainer>
  );
}

const TableContainer =
    styled.section`
    width: 100%;
  `;

const TableToolbar = styled.div`
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content:
    space-between;
  gap: 12px;
  margin-bottom: 10px;

  @media (max-width: 650px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 650px) {
    width: 100%;
    justify-content:
      space-between;
  }
`;

const TotalCount = styled.div`
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 6px;
  color: #777;
`;

const TotalCountLabel =
    styled.span`
    color: #888;
    font-size: 10px;
  `;

const SummaryCount = styled.strong`
  display: inline-flex;
  min-width: 28px;
  height: 26px;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 11px;
  font-weight: 700;
`;

const TotalCountText =
    styled.span`
    color: #777;
    font-size: 10px;
  `;

const ScrollGuide = styled.div`
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  gap: 5px;
  color: #999;
  font-size: 9px;
`;

const ScrollGuideIcon =
    styled.span`
    display: inline-flex;
    width: 19px;
    height: 19px;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e5e5;
    border-radius: 5px;
    background-color: #f9fafb;
    color: #777;
    font-size: 10px;
    font-weight: 700;
  `;

const ExcelButton = styled.button`
  display: inline-flex;
  min-width: 88px;
  height: 34px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 1px solid #d7e5d9;
  border-radius: 6px;
  background-color: #f7fbf7;
  color: #47774d;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #bcd6c0;
    background-color: #eef7ef;
  }
`;

const ExcelIcon = styled.span`
  display: inline-flex;
`;

const GridFrame = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid
    ${({$variant}) =>
    $variant === "schedule"
        ? "#f1d4c2"
        : "#e5e5e5"};
  border-radius: 8px;
  background-color: #fff;

  ${({$variant}) =>
    $variant === "schedule" &&
    `
      border-left:
        3px solid #f5811f;
      border-radius:
        0 8px 8px 0;
    `}
`;

const StyledDataGrid =
    styled(ReactDataGrid)`
    width: 100% !important;
    border: 0 !important;
    background-color: #fff;
    color: #555;
    font-size: 12px;

    .InovuaReactDataGrid__header,
    .InovuaReactDataGrid__column-header {
      background-color: #f9fafb;
    }

    .InovuaReactDataGrid__column-header {
      border-right-color:
        #eeeeee;
      color: #555;
      font-size: 11px;
      font-weight: 700;
    }

    .InovuaReactDataGrid__row {
      border-bottom-color:
        #eeeeee;
      color: #555;
      transition:
        background-color
        0.12s ease;
    }

    .InovuaReactDataGrid__row-even {
      background-color: #fcfcfc;
    }

    .InovuaReactDataGrid__row:hover {
      background-color: #fffaf6;
    }

    .InovuaReactDataGrid__cell {
      border-right-color:
        #f1f1f1;
    }

    .InovuaReactDataGrid__cell--active,
    .InovuaReactDataGrid__cell--selected {
      box-shadow:
        inset 0 0 0 1px
        #f5811f;
    }

    .InovuaReactDataGrid__footer {
      border-top-color:
        #eeeeee;
      background-color:
        #f9fafb;
    }
  `;

const EmptyState = styled.div`
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px 20px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: #aaa;
`;

const EmptyTitle = styled.div`
  margin-bottom: 6px;
  color: #555;
  font-size: 11px;
  font-weight: 700;
`;

const EmptyDescription =
    styled.div`
    color: #999;
    font-size: 9px;
    line-height: 1.5;
  `;
